import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { ThemeLogo } from 'components/theme';
import { Button } from 'components/ui';
import { doLogout } from 'utils/keycloak';

// ----------------------------------------------------------------------

const REASON_CONFIG = {
  'no-tenant':       { ns: 'noTenant',       iconColor: 'text-warning',    bgColor: 'bg-warning/10' },
  'no-roles':        { ns: 'noRoles',        iconColor: 'text-warning',    bgColor: 'bg-warning/10' },
  'no-module-access':{ ns: 'noModuleAccess', iconColor: 'text-warning',    bgColor: 'bg-warning/10' },
  'suspended':       { ns: 'accountSuspended',iconColor: 'text-red-500',   bgColor: 'bg-red-50 dark:bg-red-900/20' },
  'tenant-disabled': { ns: 'tenantDisabled', iconColor: 'text-orange-500', bgColor: 'bg-orange-50 dark:bg-orange-900/20' },
  'tenant-expired':  { ns: 'tenantExpired',  iconColor: 'text-amber-500',  bgColor: 'bg-amber-50 dark:bg-amber-900/20' },
};

function WarningIcon({ className }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  );
}

function BanIcon({ className }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
    </svg>
  );
}

function ClockIcon({ className }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function ScenarioIcon({ reason, className }) {
  if (reason === 'suspended') return <BanIcon className={className} />;
  if (reason === 'tenant-expired') return <ClockIcon className={className} />;
  return <WarningIcon className={className} />;
}

// ----------------------------------------------------------------------

export function NoTenantScreen({ reason = 'no-tenant' }) {
  const { t } = useTranslation();
  const config = REASON_CONFIG[reason] ?? REASON_CONFIG['no-tenant'];
  const { ns, iconColor, bgColor } = config;

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-white px-6 dark:bg-dark-900">
      <ThemeLogo size="large" className="mb-8 size-20" />

      <div className="max-w-md text-center">
        {/* Icon */}
        <div className={`mx-auto mb-6 flex size-16 items-center justify-center rounded-full ${bgColor}`}>
          <ScenarioIcon reason={reason} className={`size-8 ${iconColor}`} />
        </div>

        <h1 className="mb-3 text-2xl font-bold text-gray-800 dark:text-dark-100">
          {t(`${ns}.title`)}
        </h1>

        <p className="mb-2 text-gray-600 dark:text-dark-300">
          {t(`${ns}.description`)}
        </p>

        <p className="mb-8 text-sm text-gray-500 dark:text-dark-400">
          {t(`${ns}.contact`)}
        </p>

        <Button
          onClick={doLogout}
          className="w-full bg-primary-600 text-white hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600"
        >
          {t(`${ns}.signOut`)}
        </Button>
      </div>
    </div>
  );
}

NoTenantScreen.propTypes = {
  reason: PropTypes.oneOf([
    'no-tenant',
    'no-roles',
    'no-module-access',
    'suspended',
    'tenant-disabled',
    'tenant-expired',
  ]),
};
